import { User, ExtendedUser } from "../../store/types";
import { formatPhone } from "../../hooks/validation";
import StatusTag from "../StatusTag/StatusTag";
import { ColumnMeta } from "../CustomDatatable/CustomDatatable";
import { MenuItem } from "primereact/menuitem";
import { OnlineTag, OfflineTag } from "../../styles/PageLayoutStyles.styles";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { IconSend } from "../../assets/icons/Icon";
import {
  filterTextInput,
  textFilterTemplate,
  numberFilterTemplate,
  numberFilterMatchModeOptions,
} from "./FilterTemplates";

export const getUserContextMenuItems = (
  selectedUser: ExtendedUser | null,
  sendResetPasswordLink: (userId: string) => Promise<boolean>
): MenuItem[] => {
  if (!selectedUser) return [];

  const items: MenuItem[] = [];

  if (selectedUser.isActivated) {
    items.push({
      label: "Отправить ссылку сброса пароля",
      icon: <IconSend size={14} />,
      command: () => sendResetPasswordLink(selectedUser.id),
    });
  }

  return items;
};

export const isActivatedBodyTemplate = (rowData: User) => {
  const status: "success" | "danger" = rowData.isActivated
    ? "success"
    : "danger";
  const label = rowData.isActivated ? "Активирован" : "Не активирован";
  return <StatusTag status={status} label={label} />;
};

export const phoneTemplate = (rowData: any) => {
  return formatPhone(rowData?.phone);
};

export const onlineTemplate = (rowData: any) => {
  if (rowData?.isOnline)
    return (
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <OnlineTag />
        <p style={{ margin: 0 }}>Онлайн</p>
      </div>
    );
  else
    return (
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <OfflineTag />
        <p style={{ margin: 0 }}>Не онлайн</p>
      </div>
    );
};

const filterMultiselect = (value: boolean, filter: boolean[]): boolean => {
  if (!filter || filter.length === 0) return true;
  return filter.includes(value);
};

const isActivatedFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Активирован", value: true },
        { label: "Не активирован", value: false },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterCallback(e.value)}
      optionLabel="label"
      placeholder="Любой"
      className="p-column-filter"
    />
  );
};

const isOnlineFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Онлайн", value: true },
        { label: "Не онлайн", value: false },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterCallback(e.value)}
      optionLabel="label"
      placeholder="Любой"
      className="p-column-filter"
    />
  );
};

const fullName = {
  field: "fullName",
  header: "ФИО",
  filter: true,
  filterField: "fullName",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

const organization = {
  field: "organization",
  header: "Компания",
  filter: true,
  filterField: "organization",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

const licenses = {
  field: "licenses",
  header: "Количество лицензий",
  dataType: "numeric",
  filter: true,
  filterField: "licenses",
  filterElement: numberFilterTemplate,
  showFilterMatchModes: true,
  filterMatchModeOptions: numberFilterMatchModeOptions,
};

const phone = {
  field: "phone",
  header: "Телефон",
  body: phoneTemplate,
  filter: true,
  filterField: "phone",
  filterElement: textFilterTemplate,
  filterMatchMode: "contains",
  showFilterMatchModes: true,
};

const email = {
  field: "email",
  header: "Почта",
  filter: true,
  filterField: "email",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

const isActivated = {
  field: "isActivated",
  header: "Активирован",
  body: isActivatedBodyTemplate,
  filter: true,
  filterField: "isActivated",
  filterElement: isActivatedFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterMultiselect,
  showFilterMatchModes: false,
};

const isOnline = {
  field: "isOnline",
  header: "Онлайн",
  body: onlineTemplate,
  filter: true,
  filterField: "isOnline",
  filterElement: isOnlineFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterMultiselect,
  showFilterMatchModes: false,
};

export const userColumns: ColumnMeta<User>[] = [
  fullName,
  organization,
  licenses,
  phone,
  email,
  isActivated,
];

export const companyUserColumns: ColumnMeta<User>[] = [
  fullName,
  licenses,
  phone,
  email,
  isActivated,
];

export const licenseMembers: ColumnMeta<User>[] = [fullName, isOnline];
